// Email commit của repo trong workspace phải là email SSO (Luke 19/10): email commit do máy TỰ
// KHAI (`git config user.email`) — máy chưa cấu hình ra `admin@admins-mac-mini.local`, đo telemetry
// 19/10 có 16,5% commit không nhận diện được tác giả. Server đã gán bù theo người push; bước này
// chặn từ gốc cho commit MỚI.
//
// Ranh giới (KHÔNG ép mù): dev có thể chủ đích commit bằng email khách hàng cấp ⇒
//   - `sync` chỉ TỰ sửa repo có email TRỐNG hoặc email MẶC ĐỊNH của máy (*.local …);
//   - `doctor` báo mọi repo lệch SSO, `doctor --fix` mới đặt — người dùng chủ động.
// Chỉ ghi cấp REPO (`git -C <repo> config user.email`), KHÔNG đụng global.
import { spawnSync } from "node:child_process";
import { join } from "node:path";
import type { CheckResult } from "../commands/doctor.js";
import { pathExists } from "./filesystem-helpers.js";
import { readUserConfig } from "./user-config-store.js";
import { readReposManifest } from "./workspace-repos-manifest-store.js";

export const CHECK_NAME = "Email commit của repo (khớp SSO)";

export interface RepoCommitEmail {
  name: string;
  path: string;
  /** Email git THỰC DÙNG khi commit (local > global > system); null = chưa cấu hình. */
  email: string | null;
}

const norm = (e: string) => e.trim().toLowerCase();

/** Email git thực dùng trong repo. Không chạy được git / không phải repo ⇒ throw (caller xếp uncheckable). */
export function readEffectiveGitEmail(repoPath: string): string | null {
  const r = spawnSync("git", ["-C", repoPath, "config", "user.email"], { encoding: "utf8" });
  if (r.error) throw r.error;
  // exit 1 = khoá chưa đặt (hợp lệ); khác 0/1 = git lỗi thật (không phải repo…).
  if (r.status === 1) return null;
  if (r.status !== 0) throw new Error((r.stderr || `git exit ${r.status}`).trim());
  const v = r.stdout.trim();
  return v ? v : null;
}

/** Email máy tự đặt khi người dùng chưa cấu hình — không bao giờ là danh tính chủ đích. */
export function isMachineDefaultEmail(email: string | null): boolean {
  if (!email) return true;
  const e = norm(email);
  return (
    !e.includes("@") ||
    /\.(local|lan|localdomain|home)$/.test(e) ||
    e.endsWith("@localhost") ||
    e === "(none)"
  );
}

export async function readReposCommitEmail(
  workspaceRoot: string,
): Promise<{ repos: RepoCommitEmail[]; uncheckable: string[] }> {
  const repos: RepoCommitEmail[] = [];
  const uncheckable: string[] = [];
  for (const r of await readReposManifest(workspaceRoot)) {
    const repoPath = join(workspaceRoot, r.path);
    if (!(await pathExists(repoPath))) continue; // chưa clone — check exclude đã báo
    try {
      repos.push({ name: r.name, path: repoPath, email: readEffectiveGitEmail(repoPath) });
    } catch {
      uncheckable.push(r.name);
    }
  }
  return { repos, uncheckable };
}

/** Đặt user.email CẤP REPO. Trả danh sách repo đã đặt + lỗi từng repo (fail-soft, không throw). */
export function setRepoCommitEmail(
  repos: readonly RepoCommitEmail[],
  ssoEmail: string,
): { fixed: string[]; failed: { name: string; error: string }[] } {
  const fixed: string[] = [];
  const failed: { name: string; error: string }[] = [];
  for (const r of repos) {
    const res = spawnSync("git", ["-C", r.path, "config", "user.email", ssoEmail], {
      encoding: "utf8",
    });
    if (res.status === 0) fixed.push(r.name);
    else
      failed.push({
        name: r.name,
        error: (res.stderr || res.error?.message || `exit ${res.status}`).trim(),
      });
  }
  return { fixed, failed };
}

/** Bước của `sync`: chỉ sửa repo email trống/mặc định máy. Chưa login ⇒ không làm gì. */
export async function autoFixMachineDefaultCommitEmails(
  workspaceRoot: string,
  ssoEmail: string | null | undefined,
): Promise<{ fixed: string[]; failed: { name: string; error: string }[] }> {
  if (!ssoEmail?.includes("@")) return { fixed: [], failed: [] };
  const { repos } = await readReposCommitEmail(workspaceRoot);
  return setRepoCommitEmail(
    repos.filter((r) => isMachineDefaultEmail(r.email)),
    ssoEmail,
  );
}

/** Check của `doctor`: repo có email commit khác SSO ⇒ warn; `--fix` đặt email SSO cấp repo. */
export async function buildRepoCommitEmailCheck(workspaceRoot: string): Promise<CheckResult> {
  const config = await readUserConfig();
  const sso = config?.email;
  if (!sso?.includes("@")) {
    return {
      name: CHECK_NAME,
      status: "ok",
      detail: "chưa đăng nhập — bỏ qua (cần email SSO để so)",
      fixable: false,
    };
  }
  const { repos, uncheckable } = await readReposCommitEmail(workspaceRoot);
  const lech = repos.filter((r) => !r.email || norm(r.email) !== norm(sso));
  const parts = lech.map((r) => `${r.name} (${r.email ?? "chưa đặt"})`);
  if (uncheckable.length) parts.push(`không kiểm được: ${uncheckable.join(", ")}`);
  if (!parts.length) {
    return {
      name: CHECK_NAME,
      status: "ok",
      detail: repos.length
        ? `mọi repo commit bằng ${sso}`
        : "chưa có repo trong src/ — không có gì để kiểm",
      fixable: false,
    };
  }
  return {
    name: CHECK_NAME,
    status: "warn",
    detail:
      `email commit khác ${sso}: ${parts.join("; ")} — commit sẽ không nhận diện được tác giả.` +
      (lech.length ? ` Chạy 'avatar doctor --fix' (đặt cấp repo).` : "") +
      ` Repo ngoài workspace: git config --global user.email ${sso}`,
    fixable: lech.length > 0,
    fix: lech.length
      ? async () => {
          setRepoCommitEmail(lech, sso);
        }
      : undefined,
  };
}
